import { useEffect, useState } from 'react';
import { isNativeApp } from '../utils/platform';
import {
  hasStoredAccessToken,
  invalidateStaleBrowserSession,
  probeBrowserSessionVerified,
} from '../lib/browserSignInSession';

/** @typedef {'pending' | 'verified' | 'authview'} BrowserAppLayoutGate */

/**
 * AppLayout browser gate — verify session before desktop-required wall or shell shortcuts.
 * @param {{ isAuthenticated: boolean, isLoading: boolean }} auth
 * @returns {BrowserAppLayoutGate | null} null when native shell (gate not used)
 */
export function useBrowserAppLayoutGate(auth) {
  const [gate, setGate] = useState(
    /** @type {BrowserAppLayoutGate | null} */ (isNativeApp() ? null : 'pending'),
  );

  useEffect(() => {
    if (isNativeApp()) {
      setGate(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      if (auth?.isLoading) return;
      const appearsAuthed = !!auth?.isAuthenticated || hasStoredAccessToken();
      if (!appearsAuthed) {
        if (!cancelled) setGate('authview');
        return;
      }
      const ok = await probeBrowserSessionVerified();
      if (cancelled) return;
      if (ok) {
        setGate('verified');
        return;
      }
      await invalidateStaleBrowserSession();
      if (!cancelled) setGate('authview');
    })();
    return () => { cancelled = true; };
  }, [auth?.isAuthenticated, auth?.isLoading, auth?.accessToken]);

  return gate;
}
